export type MultiLangText = { text: string; language: string };
export type MultiLangPrice = { amount: number; language: string };
export type MultiLangVideo = {
  video?: { _id: string; [key: string]: any };
  videoS3?: string;
  language: string;
  processing?: boolean;
};

export type ChangeSeverity = "minor" | "major" | "critical";
export type FieldReviewStatus = "pending" | "approved" | "rejected";

export interface FieldChange {
  field: string;
  label: string;
  oldValue: any;
  newValue: any;
  category: "personal" | "professional" | "services";
  severity: ChangeSeverity;
  isMultiLang?: boolean;
  languageChanges?: LanguageChange[];
  isVideo?: boolean;
  videoChanges?: VideoLanguageChange[];
  isArray?: boolean;
  isCertificate?: boolean;
  isProviderWorkExperience?: boolean;
  isProviderProfessionalAttributes?: boolean;
  isProviderCredentials?: boolean;
  isModeratorOnlyProviderEvidence?: boolean;
  summary?: string;
  changeType?: "added" | "removed" | "modified";
}

export interface FieldReviewState {
  status: FieldReviewStatus;
  rejectionReason?: string;
}

export interface LanguageChange {
  language: string;
  oldValue: string;
  newValue: string;
  changeType: "added" | "removed" | "modified";
}

export interface VideoLanguageChange {
  language: string;
  oldVideoId?: string;
  newVideoId?: string;
  oldVideoS3?: string;
  newVideoS3?: string;
  oldVideoUrl?: string;
  newVideoUrl?: string;
  changeType: "added" | "removed" | "modified";
}

export interface CertificateChanges {
  added: any[];
  removed: any[];
  modified: any[];
  unchanged: any[];
}

/**
 * Compare two values for deep equality
 */
export const areValuesEqual = (val1: any, val2: any): boolean => {
  return JSON.stringify(val1) === JSON.stringify(val2);
};

/**
 * Compare multi-language text fields across ALL languages
 */
export const compareMultiLangTextField = (
  oldField: MultiLangText[] | undefined,
  newField: MultiLangText[] | undefined,
  fieldName: string,
  label: string,
  category: "personal" | "professional" | "services",
  severity: ChangeSeverity = "major",
): FieldChange | null => {
  if (areValuesEqual(oldField, newField)) {
    return null;
  }

  const oldMap = new Map(
    (oldField || []).map((item) => [item.language, item.text]),
  );
  const newMap = new Map(
    (newField || []).map((item) => [item.language, item.text]),
  );

  const allLanguages = new Set([...oldMap.keys(), ...newMap.keys()]);
  const languageChanges: LanguageChange[] = [];

  allLanguages.forEach((lang) => {
    const oldText = oldMap.get(lang);
    const newText = newMap.get(lang);

    if (oldText !== newText) {
      if (!oldText && newText) {
        languageChanges.push({
          language: lang,
          oldValue: "",
          newValue: newText,
          changeType: "added",
        });
      } else if (oldText && !newText) {
        languageChanges.push({
          language: lang,
          oldValue: oldText,
          newValue: "",
          changeType: "removed",
        });
      } else {
        languageChanges.push({
          language: lang,
          oldValue: oldText || "",
          newValue: newText || "",
          changeType: "modified",
        });
      }
    }
  });

  if (languageChanges.length === 0) {
    return null;
  }

  // Create summary for display
  const primaryLang = languageChanges[0];
  const summary =
    languageChanges.length > 1
      ? `${languageChanges.length} languages changed`
      : `${primaryLang.language} changed`;

  return {
    field: fieldName,
    label,
    oldValue: primaryLang.oldValue || "-",
    newValue: primaryLang.newValue || "-",
    category,
    severity,
    isMultiLang: true,
    languageChanges,
    summary,
  };
};

/**
 * Compare multi-language price fields across ALL languages
 */
export const compareMultiLangPriceField = (
  oldField: MultiLangPrice[] | undefined,
  newField: MultiLangPrice[] | undefined,
  fieldName: string,
  label: string,
  severity: ChangeSeverity = "major",
): FieldChange | null => {
  if (areValuesEqual(oldField, newField)) {
    return null;
  }

  const oldMap = new Map(
    (oldField || []).map((item) => [item.language, item.amount]),
  );
  const newMap = new Map(
    (newField || []).map((item) => [item.language, item.amount]),
  );

  const allLanguages = new Set([...oldMap.keys(), ...newMap.keys()]);
  const languageChanges: LanguageChange[] = [];

  allLanguages.forEach((lang) => {
    const oldAmount = oldMap.get(lang);
    const newAmount = newMap.get(lang);

    if (oldAmount !== newAmount) {
      if (oldAmount === undefined && newAmount !== undefined) {
        languageChanges.push({
          language: lang,
          oldValue: "",
          newValue: `$${newAmount}`,
          changeType: "added",
        });
      } else if (oldAmount !== undefined && newAmount === undefined) {
        languageChanges.push({
          language: lang,
          oldValue: `$${oldAmount}`,
          newValue: "",
          changeType: "removed",
        });
      } else {
        languageChanges.push({
          language: lang,
          oldValue: oldAmount !== undefined ? `$${oldAmount}` : "",
          newValue: newAmount !== undefined ? `$${newAmount}` : "",
          changeType: "modified",
        });
      }
    }
  });

  if (languageChanges.length === 0) {
    return null;
  }

  const primaryLang = languageChanges[0];

  return {
    field: fieldName,
    label,
    oldValue: primaryLang.oldValue || "-",
    newValue: primaryLang.newValue || "-",
    category: "professional",
    severity,
    isMultiLang: true,
    languageChanges,
  };
};

/**
 * Compare video fields across ALL languages
 */
export const compareVideoField = (
  oldField: MultiLangVideo[] | undefined,
  newField: MultiLangVideo[] | undefined,
  fieldName: string,
  label: string,
  userId: string,
  urlBuilder: (userId: string, videoId: string) => string,
  severity: ChangeSeverity = "major",
): FieldChange | null => {
  if (areValuesEqual(oldField, newField)) {
    return null;
  }

  const getVideoId = (video: MultiLangVideo | undefined) =>
    typeof video?.video === "string" ? video.video : video?.video?._id;

  const getVideoKey = (video: MultiLangVideo | undefined) =>
    video?.videoS3 ?? getVideoId(video);

  const oldMap = new Map((oldField || []).map((item) => [item.language, item]));
  const newMap = new Map((newField || []).map((item) => [item.language, item]));

  const allLanguages = new Set([...oldMap.keys(), ...newMap.keys()]);
  const videoChanges: VideoLanguageChange[] = [];

  allLanguages.forEach((lang) => {
    const oldVideo = oldMap.get(lang);
    const newVideo = newMap.get(lang);
    const oldVideoId = getVideoId(oldVideo);
    const newVideoId = getVideoId(newVideo);
    const oldVideoKey = getVideoKey(oldVideo);
    const newVideoKey = getVideoKey(newVideo);

    if (newVideoKey !== oldVideoKey) {
      videoChanges.push({
        language: lang,
        oldVideoId,
        newVideoId,
        oldVideoS3: oldVideo?.videoS3,
        newVideoS3: newVideo?.videoS3,
        oldVideoUrl: oldVideoId ? urlBuilder(userId, oldVideoId) : undefined,
        newVideoUrl: newVideoId ? urlBuilder(userId, newVideoId) : undefined,
        changeType:
          !oldVideo && newVideo
            ? "added"
            : oldVideo && !newVideo
              ? "removed"
              : "modified",
      });
    }
  });

  if (videoChanges.length === 0) {
    return null;
  }

  const primaryChange = videoChanges[0];
  const hasOldVideo = Boolean(primaryChange.oldVideoId || primaryChange.oldVideoS3);
  const hasNewVideo = Boolean(primaryChange.newVideoId || primaryChange.newVideoS3);

  return {
    field: fieldName,
    label,
    oldValue: hasOldVideo
      ? `Video (${primaryChange.language})`
      : "No video",
    newValue: hasNewVideo
      ? `Video (${primaryChange.language})`
      : "No video",
    category: "personal",
    severity,
    isVideo: true,
    videoChanges,
  };
};

/**
 * Compare primitive fields (string, number, boolean)
 */
export const comparePrimitiveField = (
  oldValue: any,
  newValue: any,
  fieldName: string,
  label: string,
  category: "personal" | "professional" | "services",
  severity: ChangeSeverity = "minor",
  formatter?: (val: any) => string,
): FieldChange | null => {
  if (areValuesEqual(oldValue, newValue)) {
    return null;
  }

  const format = formatter || ((val) => val?.toString() || "-");

  return {
    field: fieldName,
    label,
    oldValue: format(oldValue),
    newValue: format(newValue),
    category,
    severity,
  };
};

/**
 * Compare array fields (specializations, languages, etc.)
 */
export const compareArrayField = (
  oldArray: any[] | undefined,
  newArray: any[] | undefined,
  fieldName: string,
  label: string,
  category: "personal" | "professional" | "services",
  severity: ChangeSeverity = "major",
  formatter?: (arr: any[]) => string,
): FieldChange | null => {
  if (areValuesEqual(oldArray, newArray)) {
    return null;
  }

  const format =
    formatter || ((arr) => (arr?.length ? `${arr.length} items` : "-"));

  return {
    field: fieldName,
    label,
    oldValue: format(oldArray || []),
    newValue: format(newArray || []),
    category,
    severity,
    isArray: true,
  };
};

/**
 * Compare service configuration objects
 */
export const compareServiceConfig = (
  oldConfig: { switch: boolean; allowed: boolean } | undefined,
  newConfig: { switch: boolean; allowed: boolean } | undefined,
  fieldName: string,
  label: string,
  severity: ChangeSeverity = "minor",
): FieldChange | null => {
  if (areValuesEqual(oldConfig, newConfig)) {
    return null;
  }

  const formatConfig = (
    config: { switch: boolean; allowed: boolean } | undefined,
  ) => {
    if (!config) return "Not configured";
    return `Switch: ${config.switch ? "On" : "Off"}, Allowed: ${
      config.allowed ? "Yes" : "No"
    }`;
  };

  return {
    field: fieldName,
    label,
    oldValue: formatConfig(oldConfig),
    newValue: formatConfig(newConfig),
    category: "services",
    severity,
  };
};

/**
 * Compare nested object fields (like level)
 */
export const compareNestedField = (
  oldValue: any,
  newValue: any,
  fieldName: string,
  label: string,
  category: "personal" | "professional" | "services",
  severity: ChangeSeverity = "major",
  extractor: (val: any) => string,
): FieldChange | null => {
  const oldExtracted = oldValue ? extractor(oldValue) : undefined;
  const newExtracted = newValue ? extractor(newValue) : undefined;

  if (oldExtracted === newExtracted) {
    return null;
  }

  return {
    field: fieldName,
    label,
    oldValue: oldExtracted || "-",
    newValue: newExtracted || "-",
    category,
    severity,
  };
};

/**
 * Compare certificate arrays (used for appreciation, education, qualification, federation certificates)
 * These are complex arrays fetched from separate APIs, so we just detect if they need review
 * The actual detailed comparison happens in the CertificateComparisonView component
 */
export const compareCertificateField = (
  oldCertificateIds: string[] | undefined,
  newCertificateIds: string[] | undefined,
  fieldName: string,
  label: string,
  severity: ChangeSeverity = "major",
  includeWhenUnchanged = false,
): FieldChange | null => {
  // Compare just the ID arrays from SpecialistData
  if (
    areValuesEqual(oldCertificateIds, newCertificateIds) &&
    !includeWhenUnchanged
  ) {
    return null;
  }

  const oldIds = oldCertificateIds || [];
  const newIds = newCertificateIds || [];

  const added = newIds.filter((id) => !oldIds.includes(id));
  const removed = oldIds.filter((id) => !newIds.includes(id));
  // Calculate summary
  const addedCount = added.length;
  const removedCount = removed.length;
  const totalOld = oldIds.length;
  const totalNew = newIds.length;

  let summary = "";
  if (addedCount > 0 && removedCount > 0) {
    summary = `${addedCount} added, ${removedCount} removed`;
  } else if (addedCount > 0) {
    summary = `${addedCount} added`;
  } else if (removedCount > 0) {
    summary = `${removedCount} removed`;
  } else {
    summary = includeWhenUnchanged ? "Review requested" : "Modified";
  }

  return {
    field: fieldName,
    label,
    oldValue: `${totalOld} certificate(s)`,
    newValue: `${totalNew} certificate(s)`,
    category: "professional",
    severity,
    isCertificate: true,
    isArray: true,
    summary,
    changeType:
      addedCount > 0 ? "added" : removedCount > 0 ? "removed" : "modified",
  };
};
